import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_ACTIVITY_LOG } from '../../graphql';
import { useAccount } from '../../context/AccountContext';
import { Loader } from '../common/PageLoader';

/**
 * Account → Activity log (Prototype v13): one plain sentence per event — what
 * it was about by name, and an approval's version and override — then who, and
 * an area chip. The server writes the sentences (platform/services/
 * activityLog.js): only it can look up the article, post or campaign by id.
 *
 * Content is a fourth area beside the design's Approval / Settings: a person
 * writing an article or a campaign is neither a setting nor an approval. Agent
 * & rule controls is a fifth, and the one exception to one chip per area: its
 * chip says which way it moved (Loosened, Tightened, Connected), because that's
 * what people look for here.
 *
 * Who did it and what kind of change it was are two filters (backlog 7967c350):
 * the design's single row mixed them, so an agent's edit was under "Agent
 * actions" and not under "Content changes".
 */

type Area = 'approval' | 'content' | 'settings' | 'perm';
type Actor = 'person' | 'agent' | 'automatic';
type WhatFilter = 'all' | Area;
type WhoFilter = 'all' | Actor;

const WHO_FILTERS: Array<{ id: WhoFilter; label: string }> = [
  { id: 'all', label: 'Everyone' },
  { id: 'person', label: 'People' },
  { id: 'agent', label: 'Agents' },
  { id: 'automatic', label: 'Rules & automatic' },
];

const WHAT_FILTERS: Array<{ id: WhatFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'approval', label: 'Approvals' },
  { id: 'content', label: 'Content changes' },
  { id: 'settings', label: 'Settings changes' },
  { id: 'perm', label: 'Agent & rule controls' },
];

const AREA: Record<Area, string> = {
  approval: 'Approval',
  content: 'Content',
  settings: 'Settings',
  perm: 'Controls',
};

interface ActivityEntry {
  id: string;
  at: string;
  what: string;
  who: string;
  actor: Actor;
  area: Area;
  change?: string | null;
}

function whenLabel(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (date.toDateString() === now.toDateString()) return `Today ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return `Yesterday ${time}`;
  const day = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return date.getFullYear() === now.getFullYear() ? day : `${day}, ${date.getFullYear()}`;
}

/** The server marks a time inside a sentence as {{t:<ISO>}}; show it in this reader's zone. */
export function localTimes(text: string): string {
  return text.replace(/\{\{t:([^}]+)\}\}/g, (_, iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? 'a later time'
      : d.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        });
  });
}

const isWhatFilter = (v: string | null): v is WhatFilter => WHAT_FILTERS.some((f) => f.id === v);

/** Which rows a pair of filters keeps. */
export function matchesFilters(
  row: { actor: Actor; area: Area },
  who: WhoFilter,
  what: WhatFilter
): boolean {
  return (who === 'all' || row.actor === who) && (what === 'all' || row.area === what);
}

export function EventAuditSettings() {
  // ?filter=approval: Today's "Approval history →" opens on Approvals (Prototype v14).
  // ?filter=perm: Approvals' "See changes" opens on Agent & rule controls.
  // ?filter=agent: the old "Agent actions" chip, now Agents under Who.
  // Read from the address directly, so this stays free of the router (and its tests too).
  const asked = new URLSearchParams(window.location.search).get('filter');
  const [who, setWho] = useState<WhoFilter>(asked === 'agent' ? 'agent' : 'all');
  const [what, setWhat] = useState<WhatFilter>(isWhatFilter(asked) ? asked : 'all');
  const { accountId, selectedAccount, isLoading: accountsLoading } = useAccount();
  const { data, loading, error } = useQuery<{ activityLog: ActivityEntry[] }>(GET_ACTIVITY_LOG, {
    variables: { accountId, limit: 200 },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });

  const rows = useMemo(
    () =>
      (data?.activityLog || []).map((e) => ({
        ...e,
        what: localTimes(e.what),
        when: whenLabel(e.at),
        label: e.change || AREA[e.area] || AREA.content,
      })),
    [data?.activityLog]
  );

  const shown = rows.filter((row) => matchesFilters(row, who, what));

  if (accountsLoading || (loading && !data)) return <Loader label="Loading" className="ink-lede" />;

  return (
    <div>
      <h1 className="ink-h1">Activity Log</h1>
      <p className="ink-lede">
        Every change to {selectedAccount?.name || 'this account'}, by agents and people.
      </p>
      {error ? <p className="ink-lede">The activity log didn’t load. Try again.</p> : null}
      <div className="ink-act-filters" role="group" aria-label="Who made the change">
        <span className="ink-act-filter-name">Who</span>
        {WHO_FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            className="ink-chip"
            data-active={who === item.id ? 'true' : 'false'}
            onClick={() => setWho(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="ink-act-filters" role="group" aria-label="What changed">
        <span className="ink-act-filter-name">What</span>
        {WHAT_FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            className="ink-chip"
            data-active={what === item.id ? 'true' : 'false'}
            onClick={() => setWhat(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="ink-act">
        {shown.length === 0 ? (
          <p className="ink-lede" style={{ marginTop: 28 }}>
            {rows.length === 0 ? 'No activity yet.' : 'Nothing in this view.'}
          </p>
        ) : (
          shown.map((row) => (
            <div className="ink-act-row" key={row.id}>
              <span className="ink-act-when">{row.when}</span>
              <div>
                <span className="ink-act-what">{row.what}</span>
                <span className="ink-act-who"> · {row.who}</span>
              </div>
              <span className="ink-act-area">{row.label}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
